import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, FileText, FolderPlus, ArrowUp, ArrowDown, Upload, Loader2, Eye, Save, Image as ImageIcon, Video, Type, Heading1, MessageSquare } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (!u ? "" : u.startsWith("/api") ? `${BACKEND}${u}` : u);
const BLOCK_TYPES = [
  { id: "heading", label: "Heading", icon: Heading1 },
  { id: "text", label: "Text", icon: Type },
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "video", label: "Video", icon: Video },
  { id: "callout", label: "Callout", icon: MessageSquare },
];
const blankPage = { section_id: "", title: "", slug: "", hero_image: "", excerpt: "", blocks: [], seo_title: "", seo_description: "", published: false, sort_order: 0 };

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/cms/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

function ImageField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-start gap-3">
      {value ? <img src={mediaUrl(value)} alt="" className="h-14 w-14 rounded-lg object-cover border border-[#262626]" /> : <div className="h-14 w-14 rounded-lg bg-[#111] border border-[#262626] flex items-center justify-center"><ImageIcon className="h-5 w-5 text-[#444]" /></div>}
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-file`} />
          <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`} className="h-8 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white text-xs">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Upload
          </Button>
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-[#A3A3A3] hover:text-[#EF4444]">Remove</button>}
        </div>
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" data-testid={`${testid}-url`} className="bg-[#111] border-[#262626] h-8 text-xs" />
      </div>
    </div>
  );
}

export default function AdminCmsTab() {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(blankPage);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSection, setNewSection] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/admin/cms/sections"), api.get("/admin/cms/pages")])
      .then(([s, p]) => { setSections(s.data.data); setPages(p.data.data); setLoading(false); })
      .catch(() => { toast.error("Could not load content."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const addSection = async () => {
    if (!newSection.trim()) return;
    try { await api.post("/admin/cms/sections", { title: newSection.trim(), sort_order: sections.length }); setNewSection(""); toast.success("Section added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delSection = async (id) => { if (!window.confirm("Delete this section and all its pages?")) return; try { await api.delete(`/admin/cms/sections/${id}`); toast.success("Section deleted"); if (form.section_id === id) resetForm(); load(); } catch { toast.error("Could not delete."); } };

  const resetForm = () => { setForm(blankPage); setEditingId(null); };
  const newPage = (sectionId) => { setForm({ ...blankPage, section_id: sectionId || (sections[0]?.id || "") }); setEditingId(null); };
  const editPage = async (id) => {
    try { const r = await api.get(`/admin/cms/pages/${id}`); setForm({ ...blankPage, ...r.data }); setEditingId(id); }
    catch { toast.error("Could not load page."); }
  };
  const delPage = async (id) => { if (!window.confirm("Delete this page?")) return; try { await api.delete(`/admin/cms/pages/${id}`); toast.success("Page deleted"); if (editingId === id) resetForm(); load(); } catch { toast.error("Could not delete."); } };

  const savePage = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    if (!form.section_id) { toast.error("Pick a section."); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/cms/pages/${editingId}`, form);
      else { const r = await api.post("/admin/cms/pages", form); setEditingId(r.data.id); }
      toast.success("Page saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Block ops
  const addBlock = (type) => setForm((f) => ({ ...f, blocks: [...f.blocks, { type, content: "", url: "", caption: "" }] }));
  const updBlock = (i, patch) => setForm((f) => ({ ...f, blocks: f.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const rmBlock = (i) => setForm((f) => ({ ...f, blocks: f.blocks.filter((_, j) => j !== i) }));
  const moveBlock = (i, dir) => setForm((f) => { const b = [...f.blocks]; const j = i + dir; if (j < 0 || j >= b.length) return f; [b[i], b[j]] = [b[j], b[i]]; return { ...f, blocks: b }; });

  if (loading) return <Loading />;
  const sectionName = (id) => sections.find((s) => s.id === id)?.title || "—";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid="admin-cms-tab">
      {/* Left: structure */}
      <div className="space-y-4">
        <div className="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] p-4">
          <div className="flex items-center gap-2 mb-3"><FolderPlus className="h-4 w-4 text-[#007AFF]" /><h3 className="font-head font-bold text-white text-sm">Sections</h3></div>
          <div className="flex items-center gap-2 mb-3">
            <Input data-testid="cms-new-section" value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="New section name" className="bg-[#111] border-[#262626] h-8 text-xs" />
            <Button data-testid="cms-add-section" onClick={addSection} className="h-8 bg-[#007AFF] hover:bg-[#005BB5] px-3 shrink-0"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-3">
            {sections.map((s) => (
              <div key={s.id} data-testid={`cms-section-${s.id}`}>
                <div className="flex items-center justify-between group">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-[#888]">{s.title}</p>
                  <div className="flex items-center gap-1">
                    <button data-testid={`cms-add-page-${s.id}`} onClick={() => newPage(s.id)} title="Add page" className="text-[#666] hover:text-[#10B981]"><Plus className="h-3.5 w-3.5" /></button>
                    <button data-testid={`cms-del-section-${s.id}`} onClick={() => delSection(s.id)} title="Delete section" className="text-[#666] hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="mt-1 space-y-0.5">
                  {pages.filter((p) => p.section_id === s.id).map((p) => (
                    <div key={p.id} className={`flex items-center justify-between px-2 py-1.5 rounded-lg text-xs ${editingId === p.id ? "bg-[#007AFF]/15" : "hover:bg-white/5"}`}>
                      <button data-testid={`cms-edit-page-${p.id}`} onClick={() => editPage(p.id)} className="flex items-center gap-1.5 text-left flex-1 min-w-0">
                        <FileText className="h-3.5 w-3.5 text-[#666] shrink-0" />
                        <span className="text-white truncate">{p.title}</span>
                        {!p.published && <span className="text-[9px] text-[#F59E0B] shrink-0">draft</span>}
                      </button>
                      <button data-testid={`cms-del-page-${p.id}`} onClick={() => delPage(p.id)} className="text-[#666] hover:text-[#EF4444]"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {!pages.filter((p) => p.section_id === s.id).length && <p className="text-[10px] text-[#555] px-2">No pages yet</p>}
                </div>
              </div>
            ))}
            {!sections.length && <p className="text-xs text-[#666]">Add a section to begin.</p>}
          </div>
        </div>
        <a href="/learn" target="_blank" rel="noreferrer" data-testid="cms-view-site" className="flex items-center justify-center gap-2 h-9 rounded-full border border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5 text-sm"><Eye className="h-4 w-4" /> View public site</a>
      </div>

      {/* Right: page editor */}
      <div className="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] p-5" data-testid="cms-page-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-white">{editingId ? "Edit Page" : "New Page"}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => newPage()} data-testid="cms-new-page" className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white text-xs"><Plus className="h-4 w-4 mr-1" /> New</Button>
            <Button onClick={savePage} disabled={saving} data-testid="cms-save-page" className="h-9 bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold text-xs">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Title</Label>
              <Input data-testid="cms-page-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#111] border-[#262626]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Section</Label>
              <select data-testid="cms-page-section" value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                <option value="">Select…</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Excerpt (intro line)</Label>
            <Input data-testid="cms-page-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="bg-[#111] border-[#262626]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Hero image</Label>
            <ImageField value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testid="cms-hero" />
          </div>

          {/* Blocks */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Content blocks</Label>
              <div className="flex items-center gap-1">
                {BLOCK_TYPES.map((bt) => { const Icon = bt.icon; return (
                  <button key={bt.id} data-testid={`cms-add-block-${bt.id}`} onClick={() => addBlock(bt.id)} title={`Add ${bt.label}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-[#A3A3A3] hover:text-white hover:bg-white/5 border border-[#262626]"><Icon className="h-3.5 w-3.5" /> {bt.label}</button>
                ); })}
              </div>
            </div>
            {form.blocks.map((b, i) => (
              <div key={i} data-testid={`cms-block-${i}`} className="rounded-lg border border-[#1e1e1e] bg-[#0b0b0b] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-[#007AFF] font-bold">{b.type}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveBlock(i, -1)} className="text-[#666] hover:text-white"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveBlock(i, 1)} className="text-[#666] hover:text-white"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button data-testid={`cms-rm-block-${i}`} onClick={() => rmBlock(i)} className="text-[#666] hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                {(b.type === "heading" || b.type === "text" || b.type === "callout") && (
                  b.type === "heading"
                    ? <Input data-testid={`cms-block-content-${i}`} value={b.content} onChange={(e) => updBlock(i, { content: e.target.value })} placeholder="Heading text" className="bg-[#111] border-[#262626]" />
                    : <Textarea data-testid={`cms-block-content-${i}`} value={b.content} onChange={(e) => updBlock(i, { content: e.target.value })} placeholder={b.type === "callout" ? "Callout / tip text" : "Paragraph text"} className="bg-[#111] border-[#262626] min-h-[80px]" />
                )}
                {b.type === "image" && (
                  <div className="space-y-2">
                    <ImageField value={b.url} onChange={(v) => updBlock(i, { url: v })} testid={`cms-block-img-${i}`} />
                    <Input value={b.caption} onChange={(e) => updBlock(i, { caption: e.target.value })} placeholder="Caption (optional)" className="bg-[#111] border-[#262626] h-8 text-xs" />
                  </div>
                )}
                {b.type === "video" && (
                  <div className="space-y-2">
                    <Input data-testid={`cms-block-video-${i}`} value={b.url} onChange={(e) => updBlock(i, { url: e.target.value })} placeholder="Paste a YouTube / Vimeo / Loom link" className="bg-[#111] border-[#262626]" />
                    <Input value={b.caption} onChange={(e) => updBlock(i, { caption: e.target.value })} placeholder="Caption (optional)" className="bg-[#111] border-[#262626] h-8 text-xs" />
                  </div>
                )}
              </div>
            ))}
            {!form.blocks.length && <p className="text-xs text-[#555]">Add heading, text, image or video blocks to build the page.</p>}
          </div>

          {/* SEO + publish */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[#1a1a1a]">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">SEO title</Label>
              <Input data-testid="cms-seo-title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="Shown in browser tab & search" className="bg-[#111] border-[#262626]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">SEO description</Label>
              <Input data-testid="cms-seo-desc" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Meta description for search engines" className="bg-[#111] border-[#262626]" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="cms-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            <span className="text-sm text-white">Published {form.published ? "" : "(draft — hidden from public site)"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
